import { apiRequest } from '../../shared/api/client';

/**
 * Location CRUD — mirrors Invetrak.Api.Endpoints.LocationEndpoints (camelCase).
 *   GET   /api/v1/locations[?warehouseId&includeInactive]  -> LocationDto[]
 *   GET   /api/v1/locations/{id}                           -> LocationDto
 *   POST  /api/v1/locations                                -> 201 | 409 dup code
 *   PATCH /api/v1/locations/{id}                           -> LocationDto
 * Reads need locations:read, writes locations:write.
 *
 * Warehouse and code are immutable after creation — the update contract carries
 * neither, and the unique key is (WarehouseID, LocationCode), so a code is only
 * unique *within* a warehouse.
 *
 * From tbl_Location: LocationCode varchar(50), DisplayName nvarchar(200),
 * BalanceTypeID int (FK, defaults 1).
 */

export const LOCATION_CODE_MAX = 50;
export const LOCATION_NAME_MAX = 200;

export interface LocationDto {
  locationId: string;
  warehouseId: string;
  locationTypeId: string;
  locationCode: string;
  displayName: string | null;
  balanceTypeId: number;
  isForwardPick: boolean;
  /**
   * Walk order. Tenant script 065 — the number the directed pick sorts by, so it is a
   * property of the AISLE rather than of any one item. Null sorts last in both
   * directions: an unsequenced location is "somewhere else", not "first".
   */
  sequence: number | null;
  isActive: boolean;
  createdAtUtc: string;
}

export interface CreateLocationRequest {
  warehouseId: string;
  locationTypeId: string;
  locationCode: string;
  displayName?: string | null;
  balanceTypeId?: number | null;
  isForwardPick?: boolean | null;
  sequence?: number | null;
  isActive?: boolean | null;
}

export interface UpdateLocationRequest {
  locationTypeId: string;
  displayName: string | null;
  balanceTypeId: number;
  isForwardPick: boolean;
  sequence: number | null;
  isActive: boolean;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function listLocations(
  options: { warehouseId?: string; includeInactive?: boolean } = {},
  signal?: AbortSignal,
): Promise<LocationDto[]> {
  const params = new URLSearchParams();
  if (options.warehouseId) params.set('warehouseId', options.warehouseId);
  if (options.includeInactive) params.set('includeInactive', 'true');
  const query = params.toString();

  const { data } = await apiRequest<LocationDto[]>(
    `/v1/locations/${query ? `?${query}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function createLocation(
  body: CreateLocationRequest,
  signal?: AbortSignal,
): Promise<LocationDto> {
  const { data } = await apiRequest<LocationDto>('/v1/locations/', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateLocation(
  id: string,
  body: UpdateLocationRequest,
  signal?: AbortSignal,
): Promise<LocationDto> {
  const { data } = await apiRequest<LocationDto>(
    `/v1/locations/${encodeURIComponent(id)}`,
    { method: 'PATCH', body, signal, ...COOKIE_INIT },
  );
  return data;
}
